import { useEffect, useMemo, useState } from "react";
import labsysoFallback from "../../data/labsysoContent.json";
import { useCmsDoc } from "../../hooks/useCmsDoc";
import { Field, SaveBar, inputClass, textareaClass, saveCmsDoc, uploadCmsImage } from "../cmsApi.jsx";
import CmsWithMediaSpecs from "../components/CmsMediaSpecsPanel.jsx";
import { MoveButtons, moveListItem } from "../components/MoveListControls.jsx";

export default function CmsLabsysoEditor() {
  const labFallback = useMemo(
    () => ({
      hero: labsysoFallback.labsyso.hero,
      socialMedia: labsysoFallback.labsyso.socialMedia,
      about: labsysoFallback.labsyso.about,
      accreditations: labsysoFallback.labsyso.accreditations,
      preparations: labsysoFallback.labsyso.preparations || [],
    }),
    []
  );
  const examsFallback = useMemo(
    () => ({ categories: labsysoFallback.labsyso.categories || [] }),
    []
  );

  const labHook = useCmsDoc("labsyso", "lab", labFallback);
  const examsHook = useCmsDoc("labsyso", "exams", examsFallback);

  const [lab, setLab] = useState(null);
  const [exams, setExams] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (labHook.data) setLab(structuredClone(labHook.data));
  }, [labHook.data]);
  useEffect(() => {
    if (examsHook.data) setExams(structuredClone(examsHook.data));
  }, [examsHook.data]);

  if (labHook.loading || examsHook.loading || !lab || !exams) {
    return <p className="text-sm text-brand-dark/50">Cargando LABSYSO…</p>;
  }

  const categories = exams.categories || [];
  const active = categories[activeIdx];

  const strip = (obj) => {
    const { id, updatedAt, updatedBy, ...rest } = obj;
    return rest;
  };

  const onSave = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await Promise.all([
        saveCmsDoc("labsyso", "lab", strip(lab)),
        saveCmsDoc("labsyso", "exams", strip(exams)),
      ]);
      setMessage("LABSYSO guardado correctamente.");
    } catch (err) {
      setError(err?.message || "Error al guardar.");
    } finally {
      setSaving(false);
    }
  };

  const updateCategory = (patch) => {
    setExams((prev) => {
      const next = [...(prev.categories || [])];
      next[activeIdx] = { ...next[activeIdx], ...patch };
      return { ...prev, categories: next };
    });
  };

  return (
    <div className="space-y-8 max-w-5xl">
      <div>
        <h2 className="font-display text-3xl font-black">LABSYSO</h2>
        <p className="text-sm text-brand-dark/55 mt-1">Fuente: {labHook.source} / {examsHook.source}</p>
      </div>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <h3 className="font-bold text-lg">Hero / laboratorio</h3>
        <Field label="Título">
          <input
            className={inputClass}
            value={lab.hero?.title || ""}
            onChange={(e) => setLab({ ...lab, hero: { ...lab.hero, title: e.target.value } })}
          />
        </Field>
        <Field label="Subtítulo">
          <textarea
            className={textareaClass}
            value={lab.hero?.subtitle || ""}
            onChange={(e) => setLab({ ...lab, hero: { ...lab.hero, subtitle: e.target.value } })}
          />
        </Field>
        <Field label="Quiénes somos">
          <textarea
            className={textareaClass}
            value={lab.about?.quienes_somos || ""}
            onChange={(e) => setLab({ ...lab, about: { ...lab.about, quienes_somos: e.target.value } })}
          />
        </Field>
        <Field label="Misión">
          <textarea
            className={textareaClass}
            value={lab.about?.mision || ""}
            onChange={(e) => setLab({ ...lab, about: { ...lab.about, mision: e.target.value } })}
          />
        </Field>
        <Field label="Visión">
          <textarea
            className={textareaClass}
            value={lab.about?.vision || ""}
            onChange={(e) => setLab({ ...lab, about: { ...lab.about, vision: e.target.value } })}
          />
        </Field>
        <Field label="Catálogo acreditaciones (Heyzine)">
          <input
            className={inputClass}
            value={lab.accreditations?.catalog?.embedUrl || ""}
            onChange={(e) =>
              setLab({
                ...lab,
                accreditations: {
                  ...lab.accreditations,
                  catalog: { ...lab.accreditations?.catalog, embedUrl: e.target.value },
                },
              })
            }
          />
        </Field>
      </section>

      <div className="grid md:grid-cols-[240px_1fr] gap-4">
        <aside className="bg-white rounded-[28px] border border-brand-soft p-4 space-y-2">
          <div className="flex justify-between mb-2">
            <p className="text-xs font-bold uppercase tracking-widest text-brand-dark/40">Categorías</p>
            <button
              type="button"
              className="text-xs font-bold text-brand-primary"
              onClick={() => {
                setExams((prev) => ({
                  ...prev,
                  categories: [
                    ...(prev.categories || []),
                    {
                      id: `cat-${Date.now()}`,
                      title: "Nueva categoría",
                      description: "",
                      image: "",
                      exams: [],
                    },
                  ],
                }));
                setActiveIdx(categories.length);
              }}
            >
              +
            </button>
          </div>
          {categories.map((cat, idx) => (
            <div key={cat.id || idx} className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setActiveIdx(idx)}
                className={`flex-1 text-left px-3 py-2 rounded-xl text-sm font-bold ${
                  idx === activeIdx ? "bg-brand-primary text-white" : "hover:bg-brand-soft/60"
                }`}
              >
                {cat.title}
              </button>
              <MoveButtons
                idx={idx}
                total={categories.length}
                onMove={(dir) => {
                  const nextIdx = idx + dir;
                  if (nextIdx < 0 || nextIdx >= categories.length) return;
                  setExams((prev) => ({
                    ...prev,
                    categories: moveListItem(prev.categories || [], idx, dir),
                  }));
                  if (activeIdx === idx) setActiveIdx(nextIdx);
                  else if (activeIdx === nextIdx) setActiveIdx(idx);
                }}
              />
            </div>
          ))}
        </aside>

        {active && (
          <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
            <div className="flex justify-between">
              <h3 className="font-bold text-lg">Categoría / exámenes</h3>
              <button
                type="button"
                className="text-xs text-red-600 font-bold"
                onClick={() => {
                  if (!window.confirm("¿Eliminar categoría?")) return;
                  setExams((prev) => ({
                    ...prev,
                    categories: prev.categories.filter((_, i) => i !== activeIdx),
                  }));
                  setActiveIdx(0);
                }}
              >
                Eliminar
              </button>
            </div>
            <Field label="Título">
              <input className={inputClass} value={active.title || ""} onChange={(e) => updateCategory({ title: e.target.value })} />
            </Field>
            <Field label="Descripción">
              <textarea className={textareaClass} value={active.description || ""} onChange={(e) => updateCategory({ description: e.target.value })} />
            </Field>
            <CmsWithMediaSpecs variant="images">
              <Field label="URL imagen">
                <input className={inputClass} value={active.image || ""} onChange={(e) => updateCategory({ image: e.target.value })} />
              </Field>
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    const url = await uploadCmsImage("labsyso", file);
                    updateCategory({ image: url });
                  } catch (err) {
                    setError(err?.message || "Error al subir.");
                  }
                }}
              />
            </CmsWithMediaSpecs>
            <Field label="Exámenes (uno por línea)">
              <textarea
                className={`${textareaClass} min-h-[200px]`}
                value={(active.exams || []).join("\n")}
                onChange={(e) =>
                  updateCategory({
                    exams: e.target.value
                      .split("\n")
                      .map((t) => t.trim())
                      .filter(Boolean),
                  })
                }
              />
            </Field>
          </section>
        )}
      </div>

      <SaveBar saving={saving} onSave={onSave} message={message} error={error} />
    </div>
  );
}
